import { useState, useRef, useEffect } from 'react'

/**
 * CustomSelect — compact custom dropdown that replaces the native <select>.
 *
 * Props:
 *   name        – hidden input name (for FormData compatibility)
 *   value       – controlled value (pass together with onChange)
 *   defaultValue – uncontrolled initial value
 *   options     – [{ value, label }, …]
 *   onChange    – (value) => void   (optional, for controlled usage)
 *   placeholder – text shown when no option matches
 *   className   – extra class on the trigger button
 *   small       – boolean – renders a smaller (sm) variant
 *   disabled    – boolean
 */
const CustomSelect = ({
  name,
  value: controlledValue,
  defaultValue = '',
  options = [],
  onChange,
  placeholder = '— select —',
  className = '',
  small = false,
  disabled = false,
}) => {
  const isControlled = controlledValue !== undefined
  const [internalValue, setInternalValue] = useState(defaultValue)
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)

  const currentValue = isControlled ? controlledValue : internalValue
  const selected = options.find((o) => String(o.value) === String(currentValue))
  const label = selected ? selected.label : placeholder

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setOpen(false) }
    if (open) document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open])

  const select = (val) => {
    if (!isControlled) setInternalValue(val)
    onChange && onChange(val)
    setOpen(false)
  }

  const sizeClass = small ? 'cs-sm' : ''

  return (
    <div
      className={`cs-wrap ${sizeClass} ${open ? 'cs-open' : ''} ${className}`}
      ref={containerRef}
    >
      {/* Hidden input so FormData.get(name) still works */}
      {name && (
        <input type="hidden" name={name} value={currentValue} />
      )}

      {/* Trigger */}
      <button
        type="button"
        className={`cs-trigger ${sizeClass}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="cs-label">{label}</span>
        <i className={`fa fa-chevron-down cs-arrow ${open ? 'cs-arrow-up' : ''}`} aria-hidden="true" />
      </button>

      {/* Dropdown panel */}
      {open && (
        <ul className="cs-menu" role="listbox">
          {options.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={String(opt.value) === String(currentValue)}
              className={`cs-option ${String(opt.value) === String(currentValue) ? 'cs-option-active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault() // keep focus on trigger
                select(opt.value)
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default CustomSelect
